/**
 * GoalTracker Component - Monthly targets with progress bar and forecast
 */
import React, { useState } from 'react';
import type { GoalSettings, Forecast } from './types';

interface GoalTrackerProps {
  currentRevenue: number;
  currentShares: number;
  forecast: Forecast;
  goals: GoalSettings;
  onGoalsChange: (goals: GoalSettings) => void;
}

export const GoalTracker: React.FC<GoalTrackerProps> = ({
  currentRevenue, currentShares, forecast, goals, onGoalsChange
}) => {
  const [editing, setEditing] = useState(false);
  const [tempGoals, setTempGoals] = useState(goals);

  const revenueProgress = goals.monthlyRevenueTarget > 0 
    ? Math.min(100, (currentRevenue / goals.monthlyRevenueTarget) * 100) 
    : 0;
  const sharesProgress = goals.monthlySharesTarget > 0 
    ? Math.min(100, (currentShares / goals.monthlySharesTarget) * 100) 
    : 0;
  const projectedRevenueProgress = goals.monthlyRevenueTarget > 0 
    ? Math.min(100, (forecast.projectedRevenue / goals.monthlyRevenueTarget) * 100) 
    : 0;

  const confidenceColors = { low: 'text-red-400', medium: 'text-yellow-400', high: 'text-green-400' };

  const saveGoals = () => {
    onGoalsChange(tempGoals);
    setEditing(false);
  };

  if (!goals.enabled && !editing) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">🎯 Goal Tracking</h3>
          <button onClick={() => { setEditing(true); setTempGoals({ ...goals, enabled: true }); }}
            className="text-sm text-blue-400 hover:text-blue-300">
            Set Goals
          </button>
        </div>
        <p className="text-gray-500 text-sm mt-2">Set monthly targets to track your progress</p>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <h3 className="text-white font-semibold mb-4">🎯 Set Monthly Goals</h3>
        <div className="space-y-4">
          <div>
            <label className="text-white text-sm">Revenue Target ($)</label>
            <input type="number" value={tempGoals.monthlyRevenueTarget} onChange={e => setTempGoals({ ...tempGoals, monthlyRevenueTarget: Number(e.target.value) })}
              className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white" />
          </div>
          <div>
            <label className="text-white text-sm">Shares Target</label>
            <input type="number" value={tempGoals.monthlySharesTarget} onChange={e => setTempGoals({ ...tempGoals, monthlySharesTarget: Number(e.target.value) })}
              className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white" />
          </div>
          <div className="flex gap-2">
            <button onClick={saveGoals} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2 rounded">Save</button>
            <button onClick={() => setEditing(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold flex items-center gap-2">🎯 Goal Progress</h3>
        <button onClick={() => setEditing(true)} className="text-sm text-gray-400 hover:text-white">Edit</button>
      </div>

      {/* Revenue Goal */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-white">Revenue</span>
          <span className="text-white">${currentRevenue.toLocaleString()} / ${goals.monthlyRevenueTarget.toLocaleString()}</span>
        </div>
        <div className="h-3 bg-gray-700 rounded-full overflow-hidden relative">
          <div className="h-full bg-green-500 transition-all" style={{ width: `${revenueProgress}%` }} />
          {projectedRevenueProgress > revenueProgress && (
            <div className="absolute top-0 h-full bg-green-500/30 transition-all" style={{ left: `${revenueProgress}%`, width: `${projectedRevenueProgress - revenueProgress}%` }} />
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">{revenueProgress.toFixed(1)}% achieved</p>
      </div>

      {/* Shares Goal */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-white">Shares</span>
          <span className="text-white">{currentShares.toLocaleString()} / {goals.monthlySharesTarget.toLocaleString()}</span>
        </div>
        <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${sharesProgress}%` }} />
        </div>
        <p className="text-sm text-gray-500 mt-1">{sharesProgress.toFixed(1)}% achieved</p>
      </div>

      {/* Forecast */}
      <div className="bg-gray-700/50 rounded-lg p-3 mt-4">
        <p className="text-sm text-white mb-2">📊 Forecast ({forecast.daysRemaining} days left)</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-gray-500">Projected Revenue</p>
            <p className="text-white font-medium">${forecast.projectedRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </div>
          <div>
            <p className="text-gray-500">Projected Shares</p>
            <p className="text-white font-medium">{forecast.projectedShares.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          </div>
        </div>
        <p className={`text-sm mt-2 ${confidenceColors[forecast.confidence]}`}>
          Confidence: {forecast.confidence.charAt(0).toUpperCase() + forecast.confidence.slice(1)}
        </p>
      </div>
    </div>
  );
};

export default GoalTracker;

